<div class="flex flex-col gap-1 ml-2">
    <p class="text-2xl font-bold">Handbook & FAQs</p>
    <p class="text-base text-gray-500 font-normal">Find answers and guides to help you use the platform.</p>
</div>

<div class="grid grid-cols-12 mt-10">
    <div class="col-span-12 sm:col-span-12 lg:col-span-2 mb-10 ml-0 lg:ml-3 lg:mb-0">
        <div class="card flex justify-center lg:justify-center justify-start">
            <p-menu [model]="items" class="w-full lg:w-auto" />
        </div>
    </div>
    <!-- Online handbook section -->
    <div class="col-span-12 lg:col-span-10 space-y-3 border border-gray-200 rounded-lg bg-white p-5 ml-0 lg:ml-5" [class.hidden]="!showOnlineHandbook">
        <div class="flex flex-row justify-between items-center">
            <p class="font-bold text-xl">Online Handbook</p>
            <p-button 
                *hasPermission="'handbook.create'"
                label="Add New" 
                icon="pi pi-plus" 
                (click)="openCreateHandbook()" 
                class="whitespace-nowrap" />
        </div>
        <p-divider />
        
        <!-- Loading state -->
        <div *ngIf="loading" class="flex justify-center items-center py-8">
            <i class="pi pi-spin pi-spinner loading-spinner" style="font-size: 2rem"></i>
            <span class="ml-2">Loading handbooks...</span>
        </div>

        <!-- Empty state -->
        <div *ngIf="!loading && onlineHandbooks.length === 0" class="flex flex-col items-center justify-center py-8 empty-state">
            <i class="pi pi-book" style="font-size: 3rem"></i>
            <p class="text-gray-500 mt-2">No handbooks available</p>
            <!-- <p-button appGlobalLoadingDisable label="Add First Handbook" icon="pi pi-plus" (click)="openCreateHandbook()" [style]="{'background-color': 'black', 'color': 'white', 'border': 'none', 'margin-top': '10px'}" /> -->
        </div>

        <!-- Handbooks grid -->
        <div *ngIf="!loading && displayedHandbooks.length > 0" class="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <p-card *ngFor="let handbook of displayedHandbooks" [style]="{'box-shadow': 'none', 'border': '1px solid #E5E7EB'}" class="handbook-card">
                <div class="flex flex-row gap-2 items-center">
                    <div class="bg-gray-100 rounded-lg p-2 flex justify-center items-center w-12 h-14">
                        <img *ngIf="handbook.icon" [src]="handbook.icon" [alt]="handbook.title" class="w-8 h-8 handbook-icon" (error)="onHandbookImageError($event, handbook)">
                        <i *ngIf="!handbook.icon" class="pi pi-book" style="font-size: 1.5rem; color: #6B7280"></i>
                    </div>
                    <div class="flex flex-col gap-1 flex-1">
                        <p class="font-bold text-base line-clamp-1">{{ handbook.title }}</p>
                        <p class="text-sm text-gray-500 line-clamp-2">{{ handbook.description }}</p>
                    </div>
                    <div class="flex flex-col gap-1 action-buttons">
                        <p-button 
                            *hasPermission="'handbook.update'"
                            icon="pi pi-pencil" 
                            size="small" 
                            severity="secondary" 
                            (click)="openEditHandbook(handbook)"
                            [style]="{'width': '32px', 'height': '32px'}"
                            pTooltip="Edit" i18n-pTooltip>
                        </p-button>
                        <p-button 
                            *hasPermission="'handbook.delete'"
                            icon="pi pi-trash" 
                            size="small" 
                            severity="secondary" 
                            (click)="deleteHandbook(handbook)"
                            [style]="{'width': '32px', 'height': '32px'}"
                            pTooltip="Delete" i18n-pTooltip
                            class="delete-btn">
                        </p-button>
                    </div>
                </div>
                <div class="mt-3 p-1 pl-14 flex gap-2 items-center cursor-pointer" (click)="window.open(handbook.sourceLink, '_blank')">
                    <span class="text-base text-black" >More Information</span>
                    <i class="pi pi-arrow-right text-sm"></i>
                </div>
            </p-card>
        </div>

        <!-- Load More Button -->
        <div *ngIf="!loading && hasMoreHandbooks" class="flex justify-center mt-6">
            <div class="flex flex-col items-center gap-2">
                <p class="text-sm text-gray-500">
                    Showing {{ displayedHandbooks.length }} of {{ onlineHandbooks.length }} handbooks
                </p>
                <p-button appGlobalLoadingDisable 
                    label="Load More" 
                    icon="pi pi-plus" 
                    (click)="loadMoreHandbooks()"
                    class="load-more-btn">
                </p-button>
            </div>
        </div>
    </div>

    <!-- FAQs section -->
    <div class="col-span-12 lg:col-span-10 space-y-3 border border-gray-200 rounded-lg bg-white p-5 ml-0 lg:ml-5" [class.hidden]="!showFaqs">
        <div class="flex flex-row justify-between items-center">
            <p class="font-bold text-xl">Frequently Asked Questions</p>
            <p-button 
                *hasPermission="'faq.create'"
                label="Add New" 
                icon="pi pi-plus" 
                (click)="openCreateFaq()" 
                class="whitespace-nowrap" />
        </div>
        <p-divider />
        
        <!-- Loading state -->
        <div *ngIf="faqLoading" class="flex justify-center items-center py-8">
            <i class="pi pi-spin pi-spinner loading-spinner" style="font-size: 2rem"></i>
            <span class="ml-2">Loading FAQs...</span>
        </div>

        <!-- Empty state -->
        <div *ngIf="!faqLoading && faqs.length === 0" class="flex flex-col items-center justify-center py-8 empty-state">
            <i class="pi pi-question-circle" style="font-size: 3rem"></i>
            <p class="text-gray-500 mt-2">No FAQs available</p>
        </div>

        <!-- FAQs accordion -->
        <div *ngIf="!faqLoading && displayedFaqs.length > 0" class="w-full overflow-hidden">
            <p-accordion class="w-full">
                <p-accordion-panel *ngFor="let faq of displayedFaqs; let i = index" [value]="i.toString()" class="w-full">
                    <p-accordion-header>
                        <div class="flex justify-between items-center w-full">
                            <span class="flex-1 mr-4 break-words">{{ faq.question }}</span>
                            <div class="flex gap-2 flex-shrink-0 mr-5">
                                <p-button 
                                    *hasPermission="'faq.update'"
                                    icon="pi pi-pencil" 
                                    size="small" 
                                    severity="secondary" 
                                    (click)="openEditFaq(faq)"
                                    [style]="{'width': '24px', 'height': '24px'}"
                                    pTooltip="Edit" i18n-pTooltip>
                                </p-button>
                                <p-button 
                                    *hasPermission="'faq.delete'"
                                    icon="pi pi-trash" 
                                    size="small" 
                                    severity="secondary" 
                                    (click)="deleteFaq(faq)"
                                    [style]="{'width': '24px', 'height': '24px'}"
                                    pTooltip="Delete" i18n-pTooltip
                                    class="delete-btn">
                                </p-button>
                            </div>
                        </div>
                    </p-accordion-header>
                    <p-accordion-content>
                        <div class="w-full overflow-hidden">
                            <div class="break-words overflow-wrap-anywhere" [innerHTML]="sanitizeHtml(faq.answer)"></div>
                        </div>
                    </p-accordion-content>
                </p-accordion-panel>
            </p-accordion>
        </div>

        <!-- Load More Button for FAQs -->
        <div *ngIf="!faqLoading && hasMoreFaqs" class="flex justify-center mt-6">
            <div class="flex flex-col items-center gap-2">
                <p class="text-sm text-gray-500">
                    Showing {{ displayedFaqs.length }} of {{ faqs.length }} FAQs
                </p>
                <p-button appGlobalLoadingDisable 
                    label="Load More" 
                    icon="pi pi-plus" 
                    (click)="loadMoreFaqs()"
                    class="load-more-btn">
                </p-button>
            </div>
        </div>
    </div>
    <!-- Video guides section -->
    <div class="col-span-12 lg:col-span-10 space-y-3 border border-gray-200 rounded-lg bg-white p-5 ml-0 lg:ml-5" [class.hidden]="!showVideoGuides">
        <div class="flex flex-row justify-between items-center">
            <p class="font-bold text-xl">Video Guides</p>
            <p-button 
                *hasPermission="'videoguide.create'"
                label="Add New" 
                icon="pi pi-plus" 
                (click)="openCreateVideoGuide()" 
                class="whitespace-nowrap" />
        </div>
        <p-divider />
        
        <!-- Loading state -->
        <div *ngIf="videoGuideLoading" class="flex justify-center items-center py-8">
            <i class="pi pi-spin pi-spinner loading-spinner" style="font-size: 2rem"></i>
            <span class="ml-2">Loading video guides...</span>
        </div>

        <!-- Empty state -->
        <div *ngIf="!videoGuideLoading && videoGuides.length === 0" class="flex flex-col items-center justify-center py-8 empty-state">
            <i class="pi pi-video" style="font-size: 3rem"></i>
            <p class="text-gray-500 mt-2">No video guides available</p>
        </div>

        <!-- Video guides grid -->
        <div *ngIf="!videoGuideLoading && displayedVideoGuides.length > 0" class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <p-card *ngFor="let videoGuide of displayedVideoGuides" [style]="{'box-shadow': 'none', 'border': 'none'}" class="video-card">
                <div class="relative aspect-video w-full overflow-hidden rounded-lg">
                    <a href="{{videoGuide.sourceLink}}" target="_blank" class="block w-full h-full">
                        <img class="w-full h-full object-cover" [src]="videoGuide.thumbnail" [alt]="videoGuide.title" (error)="onVideoGuideImageError($event, videoGuide)">
                    </a>
                    <div class="absolute top-2 left-2 flex gap-2">
                        <p-button 
                            *hasPermission="'videoguide.update'"
                            icon="pi pi-pencil" 
                            size="small" 
                            severity="secondary" 
                            (click)="openEditVideoGuide(videoGuide)"
                            [style]="{'width': '32px', 'height': '32px'}"
                            pTooltip="Edit" i18n-pTooltip>
                        </p-button>
                        <p-button 
                            *hasPermission="'videoguide.delete'"
                            icon="pi pi-trash" 
                            size="small" 
                            severity="secondary" 
                            (click)="deleteVideoGuide(videoGuide)"
                            [style]="{'width': '32px', 'height': '32px'}"
                            pTooltip="Delete" i18n-pTooltip
                            class="delete-btn">
                        </p-button>
                    </div>
                </div>
                
                <a href="{{videoGuide.sourceLink}}" target="_blank">
                    <p class="text-sm mt-2 line-clamp-3">{{ videoGuide.title }}</p>
                </a>
                </p-card>
        </div>

        <!-- Load More Button for Video Guides -->
        <div *ngIf="!videoGuideLoading && hasMoreVideoGuides" class="flex justify-center mt-6">
            <div class="flex flex-col items-center gap-2">
                <p class="text-sm text-gray-500">
                    Showing {{ displayedVideoGuides.length }} of {{ videoGuides.length }} video guides
                </p>
                <p-button appGlobalLoadingDisable 
                    label="Load More" 
                    icon="pi pi-plus" 
                    (click)="loadMoreVideoGuides()"
                    class="load-more-btn">
                </p-button>
            </div>
        </div>
    </div>
</div>

<!-- Online hand book drawer -->
<app-drawer 
    #onlineHandbookDrawer
    [header]="editingHandbook ? 'Edit Handbook' : 'Add New Handbook'" 
    [(visible)]="onlineHandBookDrawer" 
    [fields]="onlineHandbookFields"
    [initialData]="editingHandbook"
    submitLabel="Submit"
    (submit)="onOnlineHandbookSubmit($event)">
</app-drawer>

<!-- FAQ drawer -->
<app-drawer 
    #faqDrawerComponent
    [header]="editingFaq ? 'Edit FAQ' : 'Add New FAQ'" 
    [(visible)]="faqDrawer" 
    [fields]="faqFields"
    [initialData]="editingFaq"
    submitLabel="Submit"
    (submit)="onFaqSubmit($event)">
</app-drawer>

 <!-- Video guide drawer -->
<app-drawer 
    #videoGuideDrawerComponent
    [header]="editingVideoGuide ? 'Edit Video Guide' : 'Add New Video Guide'" 
    [(visible)]="videoGuideDrawer" 
    [fields]="videoGuideFields"
    [initialData]="editingVideoGuide"
    submitLabel="Submit"
    (submit)="onVideoGuideSubmit($event)">
</app-drawer>

<!-- Toast for notifications -->
<p-toast></p-toast>

<!-- Confirmation dialog -->
<p-confirmDialog></p-confirmDialog>